import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { useAcceptInvitationMutation } from 'app/store/reducers/api/config-manager-cloud-services/store/invitations';
import { useSwitchAccount } from 'app/shared/hooks/account/useSwitchAccount';

/** Landing page for an invitation link: /invitations/:token/accept */
export const AcceptInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [acceptInvitation] = useAcceptInvitationMutation();
  const { switchTo } = useSwitchAccount();
  const [state, setState] = useState<'pending' | 'success' | 'error'>('pending');
  const [message, setMessage] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (started.current || !token) return;
    started.current = true;
    acceptInvitation({ token })
      .unwrap()
      .then(async result => {
        if (result?.accountId) {
          await switchTo(result.accountId).catch(() => undefined);
        }
        setState('success');
      })
      .catch(err => {
        setState('error');
        setMessage(err?.data?.detail ?? 'This invitation could not be accepted.');
      });
  }, [token, acceptInvitation, switchTo]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '70vh', gap: 2 }}>
      {state === 'pending' && (
        <>
          <CircularProgress />
          <Typography>Accepting your invitation…</Typography>
        </>
      )}
      {state === 'success' && (
        <>
          <Typography variant="h6">You've joined the account.</Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            Continue
          </Button>
        </>
      )}
      {state === 'error' && (
        <>
          <Typography variant="h6" color="error">
            {message}
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/')}>
            Go home
          </Button>
        </>
      )}
    </Box>
  );
};

export default AcceptInvitation;
